import React, { useEffect, useState } from "react";
import { resolveActiveStepMode, STEP_MODE } from "../utils/ergMode";
import { Row, Col, Tag } from "antd";
import {
  AlertOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  HeartFilled,
  Loading3QuartersOutlined,
  MinusOutlined,
  ThunderboltOutlined,
  TrophyFilled,
} from "@ant-design/icons";

const DESKTOP_DARK_BLUE = "rgba(0,0,65,1)";
const GOLD = "rgba(255, 215, 0, 1)";

const getColor = (intensity, FTP) => {
  if (!Number.isFinite(Number(intensity)) || !Number.isFinite(Number(FTP)) || FTP <= 0) {
    return "rgba(128, 128, 128, 1)";
  }

  if (intensity >= FTP * 1.5) return "rgba(128, 0, 128, 1)";
  if (intensity >= FTP * 1.19) return "rgba(255, 0, 0, 1)";
  if (intensity >= FTP * 1.05) return "rgba(255, 165, 0, 1)";
  if (intensity >= FTP * 0.9) return "rgba(255, 215, 0, 1)";
  if (intensity >= FTP * 0.75) return "rgba(0, 128, 0, 1)";
  if (intensity > FTP * 0.6) return "rgba(153, 204, 255, 1)";
  return "rgba(128, 128, 128, 1)";
};

const extractRPM = (text) => {
  if (!text) return null;
  const rpmRegex = /(\d+)\s*RPM/i;
  const match = String(text).match(rpmRegex);
  return match ? parseInt(match[1], 10) : null;
};

// Create a translucent variant of an rgba() color
function withAlpha(rgbaColor, alpha) {
  const match = String(rgbaColor).match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)/i
  );
  if (!match) return rgbaColor;
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const a = typeof alpha === "number" ? alpha : match[4] ? parseFloat(match[4]) : 1;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export default function HomeTrainerScreenControl({
  isMobile = false,
  steps = [],
  indexStep = 0,
  totTimeStep = 0,
  remainingTimeStep = 0,
  currentTargetPower = 0,
  nextTargetPower = null,
  powerInfo,
  pause = true,
  FTP = 0,
  formatTime,
  bpm, // optional; if missing, we'll try to read latest sample from powerInfo.data
  headerSuffix = "",
  tagClassName,
  screenWidthStyle, // e.g. "calc(100% - 20px)" on mobile
  noTrainer = false,
}) {
  const safeSteps = Array.isArray(steps) ? steps : [];
  const currentStep =
    indexStep >= 0 && indexStep < safeSteps.length ? safeSteps[indexStep] : null;

  const currentPower = Math.round(Number(powerInfo?.slidding) || 0);
  const targetPower = Math.round(Number(currentTargetPower) || 0);

  // Computed here rather than passed in: this component already has both inputs.
  const activeStepMode = resolveActiveStepMode(safeSteps, indexStep);
  const ergIsDriving = activeStepMode === STEP_MODE.ERG;
  const MODE_PILL_LABEL = { free: "FREE RIDE", max: "BEST EFFORT" };

  const isWorkoutCompleted =
    safeSteps.length > 0 &&
    (indexStep >= safeSteps.length ||
      (currentStep && Number(currentStep.duration) - Number(totTimeStep) < 0));

  // When the workout becomes completed, flash a big "trophy" completion overlay for a few seconds.
  const [completedFlashUntil, setCompletedFlashUntil] = useState(0);
  useEffect(() => {
    if (isWorkoutCompleted) {
      setCompletedFlashUntil(Date.now() + 3000);
    }
  }, [isWorkoutCompleted]);
  const showBigCompleted = isWorkoutCompleted && Date.now() < completedFlashUntil;

  // During the first seconds of a new interval, briefly show a single big target watt cue.
  const elapsedInStep = Number(totTimeStep);
  const showBigTarget =
    !pause &&
    !isWorkoutCompleted &&
    !showBigCompleted &&
    targetPower > 0 &&
    Number.isFinite(elapsedInStep) &&
    elapsedInStep >= 0 &&
    elapsedInStep < 3;

  const cadenceRaw = Number(powerInfo?.sliddingCad);
  const cadenceValid =
    Number.isFinite(cadenceRaw) && cadenceRaw > 10 && cadenceRaw < 300
      ? Math.round(cadenceRaw)
      : null;

  const latestSample =
    Array.isArray(powerInfo?.data) && powerInfo.data.length > 0
      ? powerInfo.data[powerInfo.data.length - 1]
      : null;
  const bpmCandidate = bpm != null ? bpm : latestSample?.bpm;
  const bpmRaw = Number(bpmCandidate);
  const bpmValid = Number.isFinite(bpmRaw) ? Math.round(bpmRaw) : null;

  const avgTotRaw = Number(powerInfo?.avgTot);
  const avgTot = Number.isFinite(avgTotRaw) ? Math.round(avgTotRaw) : 0;

  const avgLapRaw =
    Array.isArray(powerInfo?.avgSteps) && powerInfo.avgSteps.length > 0
      ? Number(powerInfo.avgSteps[powerInfo.avgSteps.length - 1])
      : 0;
  const avgLap = Number.isFinite(avgLapRaw) ? Math.round(avgLapRaw) : 0;

  const targetRpm = extractRPM(currentStep?.comment);

  const powerStatus = (() => {
    if (pause) return { kind: "paused", text: "Paused", color: "rgba(0,0,0,0.35)", icon: <PauseIcon /> };
    // With no trainer connected, currentPower is a recorded zero, not a
    // reading, so it can never be compared against the target. The target
    // itself is still shown (the pill above, and the step overlay in large
    // type on an ERG step) -- what is missing here is the reading, not the
    // target, so say that instead of reusing "no target power" below.
    if (noTrainer) return { kind: "none", text: "No power reading", color: "rgba(0,0,0,0.35)", icon: <MinusOutlined /> };
    if (!(targetPower > 0)) return { kind: "none", text: "No target power", color: "rgba(0,0,0,0.35)", icon: <MinusOutlined /> };
    if (currentPower > targetPower * 1.1) return { kind: "down", text: "Ease off", color: "#ff4d4f", icon: <ArrowDownOutlined /> };
    if (currentPower < targetPower * 0.9) return { kind: "up", text: "Push more", color: "#ff4d4f", icon: <ArrowUpOutlined /> };
    return { kind: "ok", text: "On target", color: "rgba(0,0,0,0.55)", icon: <CheckCircleOutlined /> };
  })();

  const cadenceStatus = (() => {
    if (pause) return { kind: "paused", text: "Paused", color: "rgba(0,0,0,0.35)", icon: <PauseIcon /> };
    if (!Number.isFinite(targetRpm)) return { kind: "none", text: "No target cadence", color: "rgba(0,0,0,0.35)", icon: <MinusOutlined /> };
    if (cadenceValid == null) return { kind: "none", text: "No cadence signal", color: "rgba(0,0,0,0.35)", icon: <AlertOutlined /> };
    if (cadenceValid > targetRpm + 5) return { kind: "down", text: "Spin slower", color: "#1677ff", icon: <ArrowDownOutlined /> };
    if (cadenceValid < targetRpm - 5) return { kind: "up", text: "Spin faster", color: "#1677ff", icon: <ArrowUpOutlined /> };
    return { kind: "ok", text: "Cadence ok", color: "rgba(0,0,0,0.55)", icon: <CheckCircleOutlined /> };
  })();

  const warningColor =
    !noTrainer &&
    indexStep < safeSteps.length &&
    !pause &&
    targetPower > 0 &&
    (currentPower > targetPower * 1.1 || currentPower < targetPower * 0.9)
      ? "red"
      : DESKTOP_DARK_BLUE;

  const showNext = indexStep < safeSteps.length - 1 && remainingTimeStep < 5;
  const fmt = typeof formatTime === "function" ? formatTime : (s) => String(s);

  const tagCls = tagClassName || (isMobile ? "custom-tag-phone" : "custom-tag");
  const powerFont = isMobile ? 26 : 36;
  const bigTargetFont = isMobile ? 52 : 80;
  const subFont = isMobile ? 13 : 14;
  const lineH = isMobile ? 18 : 18;

  // Keep the screen border always "blue-ish" for a stable futuristic HUD.
  // Only the target badge should reflect the power zone color.
  const zoneColor = getColor(targetPower, FTP);
  const zoneGlow = zoneColor.replace("1)", "0.28)");
  const hudBorder = "rgba(111, 144, 175, 0.85)";
  const hudGlow = "rgba(24, 144, 255, 0.22)";

  const StepMetaPill = ({ children }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.55)",
        border: "1px solid rgba(0,0,0,0.06)",
        fontSize: isMobile ? 12 : 12,
        color: "rgba(0,0,0,0.72)",
      }}
    >
      {children}
    </span>
  );

  const TargetMetaPill = ({ children }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 999,
        background: zoneColor.replace("1)", "0.14)"),
        border: `1px solid ${zoneColor.replace("1)", "0.55)")}`,
        boxShadow: `0 0 0 1px rgba(255,255,255,0.35) inset, 0 10px 18px ${zoneGlow}`,
        fontSize: isMobile ? 12 : 12,
        color: "rgba(0,0,0,0.78)",
      }}
    >
      {children}
    </span>
  );

  function PauseIcon() {
    return <Loading3QuartersOutlined spin={false} style={{ opacity: 0.8 }} />;
  }

  return (
    <div>
      <div
        className="screen-div glass-outline"
        style={{
          position: "relative",
          width: screenWidthStyle,
          color: DESKTOP_DARK_BLUE,
          background: `linear-gradient(135deg, rgba(255,255,255,0.78), rgba(235,245,255,0.72))`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          border: `5px solid ${hudBorder}`,
          borderRadius: 16,
          boxShadow: `0 0 0 1px rgba(255,255,255,0.55) inset, 0 10px 26px rgba(0,0,0,0.10), 0 0 22px ${hudGlow}`,
          padding: isMobile ? "10px 12px" : "12px 14px",
          lineHeight: `${lineH}px`,
        }}
      >
        {/* Keep the normal layout mounted so the screen height never changes.
            During the first seconds, hide it (visibility) and overlay the big target cue. */}
        <div style={showBigTarget || showBigCompleted ? { visibility: "hidden" } : undefined}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <StepMetaPill>
                Step <b>{indexStep + 1}</b> / <b>{safeSteps.length}</b>
              </StepMetaPill>
              {isWorkoutCompleted ? (
                <StepMetaPill>
                  <CheckCircleOutlined /> <b>WORKOUT COMPLETED</b>
                </StepMetaPill>
              ) : ergIsDriving ? (
                <TargetMetaPill>
                  <ThunderboltOutlined /> Target <b>{targetPower}</b>W
                </TargetMetaPill>
              ) : (
                <TargetMetaPill>
                  <ThunderboltOutlined /> <b>{MODE_PILL_LABEL[activeStepMode]}</b>
                </TargetMetaPill>
              )}
              {headerSuffix ? <StepMetaPill>{headerSuffix}</StepMetaPill> : null}
            </div>
            <div style={{ fontSize: subFont, color: "rgba(0,0,0,0.45)" }}>
              {pause ? "PAUSED" : "LIVE"}
            </div>
          </div>

          {/* Main power (centered) */}
          <div
            style={{
              display: "grid",
              // minmax(0,1fr) prevents side columns from forcing min-content width and shifting the center on small screens
              gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
              alignItems: "center",
              gap: 10,
              marginTop: isMobile ? 10 : 20,
            }}
          >
            {/* left spacer (keeps center block perfectly centered) */}
            <div style={{ minWidth: 0 }} />

            {/* centered power */}
            <div style={{ justifySelf: "center", textAlign: "center" }}>
              <b style={{ fontSize: powerFont, color: warningColor, letterSpacing: 0.2 }}>
                {currentPower}W
              </b>
            </div>

            <div
              style={{
                justifySelf: "stretch",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 10,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {/* status (to the right of centered power) */}
              <span
                style={{
                  fontSize: subFont,
                  color: powerStatus.color,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {powerStatus.icon} {powerStatus.text}
              </span>
            </div>
          </div>

          {/* Cadence guidance (fixed height) */}
          <div style={{ marginTop: 4, minHeight: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            {/* Cadence indicator (LEFT only) */}
            <span style={{ fontSize: subFont, color: cadenceStatus.color, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <DashboardOutlined />
              {Number.isFinite(targetRpm) ? (
                <>
                  <b>{targetRpm}rpm</b> · {cadenceStatus.text}
                </>
              ) : (
                <>{cadenceStatus.text}</>
              )}
            </span>

            {/* Next/Remaining (RIGHT) */}
            <span style={{ fontSize: subFont, color: showNext ? "#cf1322" : "rgba(0,0,0,0.45)", textAlign: "right", whiteSpace: "nowrap" }}>
              {showNext && nextTargetPower != null ? (
                <>
                  Next <ArrowRightOutlined /> <b>{Math.round(Number(nextTargetPower) || 0)}W</b> in {fmt(remainingTimeStep)}
                </>
              ) : (
                <span style={{ opacity: remainingTimeStep >= 5 ? 1 : 0 }}>{`Remaining: ${fmt(remainingTimeStep)}`}</span>
              )}
            </span>
          </div>

          {/* Comment (fixed height) */}
          <div style={{ marginTop: 4, minHeight: 18 }}>
            <span style={{ fontSize: subFont, color: hudBorder }}>
              {safeSteps.length === 0
                ? "No training"
                : indexStep >= safeSteps.length || (currentStep && Number(currentStep.duration) - Number(totTimeStep) < 0)
                  ? "Training over, well done!"
                  : currentStep?.comment
                    ? currentStep.comment
                    : "🚲"}
            </span>
          </div>
        </div>

        {showBigTarget ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: isMobile ? "12px 12px" : "18px 14px",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
                padding: isMobile ? "10px 14px" : "12px 18px",
                borderRadius: 18,
                background: zoneColor.replace("1)", "0.10)"),
                boxShadow: `0 0 0 1px rgba(255,255,255,0.45) inset, 0 18px 32px ${zoneGlow}`,
              }}
            >
              <span
                style={{
                  fontSize: isMobile ? 12 : 13,
                  letterSpacing: 2,
                  fontWeight: 700,
                  color: "rgba(0,0,0,0.55)",
                }}
              >
                TARGET
              </span>
              <span
                style={{
                  fontSize: bigTargetFont,
                  fontWeight: 800,
                  lineHeight: 1,
                  color: zoneColor,
                  textShadow: `0 0 18px ${zoneGlow}`,
                  letterSpacing: 0.4,
                }}
              >
                <ThunderboltOutlined /> {targetPower}W
              </span>
            </div>
          </div>
        ) : null}

        {showBigCompleted ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: isMobile ? "12px 12px" : "18px 14px",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
                padding: isMobile ? "14px 14px" : "16px 18px",
                borderRadius: 18,
                background: `linear-gradient(180deg, ${withAlpha(GOLD, 0.22)}, rgba(255,255,255,0.18))`,
                border: `1px solid ${withAlpha(GOLD, 0.45)}`,
                boxShadow: `0 10px 30px ${withAlpha(GOLD, 0.18)}, 0 10px 26px rgba(0,0,0,0.10)`,
              }}
            >
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(180deg, ${withAlpha(GOLD, 0.45)}, ${withAlpha(GOLD, 0.18)})`,
                  border: `1px solid ${withAlpha(GOLD, 0.55)}`,
                  boxShadow: `0 8px 18px ${withAlpha(GOLD, 0.18)}`,
                  color: "rgba(0,0,0,0.78)",
                  fontSize: 20,
                }}
              >
                <TrophyFilled />
              </span>
              <span style={{ fontSize: isMobile ? 12 : 13, letterSpacing: 2, fontWeight: 700, color: "rgba(0,0,0,0.55)" }}>
                WORKOUT COMPLETED
              </span>
              <span style={{ fontSize: isMobile ? 28 : 34, fontWeight: 800, lineHeight: 1.05, color: "rgba(0,0,0,0.82)" }}>
                Nice work!
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Stats squares */}
      <div style={{ width: screenWidthStyle, marginTop: 8 }}>
        <Row gutter={[6, 6]}>
          <Col span={12}>
            <Tag
              color={DESKTOP_DARK_BLUE}
              className={tagCls}
              style={{ width: "100%", textAlign: "center", padding: isMobile ? "4px 8px" : "5px 10px", borderRadius: 12, fontSize: isMobile ? 12 : 12 }}
            >
              <ThunderboltOutlined /> Avg {avgTot}W
            </Tag>
          </Col>
          <Col span={12}>
            <Tag
              color={DESKTOP_DARK_BLUE}
              className={tagCls}
              style={{ width: "100%", textAlign: "center", padding: isMobile ? "4px 8px" : "5px 10px", borderRadius: 12, fontSize: isMobile ? 12 : 12 }}
            >
              <ThunderboltOutlined /> Avg Lap {avgLap}W
            </Tag>
          </Col>
          <Col span={12}>
            <Tag
              color={DESKTOP_DARK_BLUE}
              className={tagCls}
              style={{ width: "100%", textAlign: "center", padding: isMobile ? "4px 8px" : "5px 10px", borderRadius: 12, fontSize: isMobile ? 12 : 12 }}
            >
              <DashboardOutlined /> RPM {cadenceValid ?? 0}
            </Tag>
          </Col>
          <Col span={12}>
            <Tag
              color={DESKTOP_DARK_BLUE}
              className={tagCls}
              style={{ width: "100%", textAlign: "center", padding: isMobile ? "4px 8px" : "5px 10px", borderRadius: 12, fontSize: isMobile ? 12 : 12 }}
            >
              <HeartFilled style={{ color: "rgba(255,77,79,0.95)" }} /> BPM {bpmValid ?? 0}
            </Tag>
          </Col>
        </Row>
      </div>
    </div>
  );
}


